import type { ChainNetwork } from '@ducat-unit/core';
import { memoize } from 'lodash';

import { getGuardianNumFromUrl, getRandomIndex } from '@/components/layout/header/settings/NetworkSettings/utils';
import { ALLOW_GUARDIAN_RANDOMIZE, DISABLED_GUARDIAN_NUMS, GUARDIAN_NUM_FORCED, IS_DEV } from '@/constants';

import type { NetworkSettingsConfig } from './config.types';
import { type ConfigNetworkName, configsByNetwork } from './configMatrix';

function normalizeConfigNetwork(network: ChainNetwork | string | undefined): ConfigNetworkName | undefined {
  switch (network) {
    case 'regtest':
    case 'mutiny':
    case 'testnet4':
    case 'main':
      return network;
    case 'mutinynet':
      return 'mutiny';
    case 'mainnet':
    case 'alpha-mainnet':
      return 'main';
    default:
      return undefined;
  }
}

function interpolateGuardianUrl(template: string, network: ChainNetwork, number: string) {
  return template.replace('{network}', network).replace('{num}', number);
}

function getConfigFn(network: ChainNetwork | string | undefined): (NetworkSettingsConfig & { isDev: boolean }) | undefined {
  const configNetwork = normalizeConfigNetwork(network);
  if (!configNetwork) return undefined;

  const config = configsByNetwork[configNetwork];

  const guardianUrlTemplate = config.guardianUrlTemplate;
  const isGuardianTemplateAvailable = guardianUrlTemplate.includes('{num}');

  const allGuardians = isGuardianTemplateAvailable
    ? new Array(4).fill(0).map((_, i) => ({
        guardianUrl: interpolateGuardianUrl(guardianUrlTemplate, configNetwork, (i + 1).toString()),
      }))
    : [{ guardianUrl: guardianUrlTemplate }];

  const guardians = allGuardians.filter((v) => !DISABLED_GUARDIAN_NUMS.includes(getGuardianNumFromUrl(v.guardianUrl) || ''));

  const guardianForced =
    GUARDIAN_NUM_FORCED && GUARDIAN_NUM_FORCED <= allGuardians.length ? [allGuardians[GUARDIAN_NUM_FORCED - 1]] : undefined;

  // Randomize guardians (will work if the num is not forced):
  const guardianIndex = ALLOW_GUARDIAN_RANDOMIZE ? getRandomIndex(guardians) : 0;

  const resultConfig = {
    ...config,
    isDev: IS_DEV,
    guardians: guardianForced ?? guardians,
    guardianUrl: (guardianForced || guardians)[guardianIndex].guardianUrl,
    guardMemberPubkey: '',
  };

  return resultConfig;
}

export const getConfig = memoize(getConfigFn);
